import type { AdminUser, Paginated } from "@lucky-try/contracts";
import { ROLES } from "@lucky-try/shared";
import { PlusIcon } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ConfirmDialog, Dialog } from "../components/ui/dialog";
import { Checkbox, Field, Input, Select } from "../components/ui/form";
import {
  Badge,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  Pagination,
  Toolbar,
} from "../components/ui/misc";
import { api, errorMessage } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { useCan, useMe } from "../lib/session";

export function UsersPage() {
  const search = useSearch({ from: "/authed/users" });
  const navigate = useNavigate({ from: "/users" });
  const queryClient = useQueryClient();
  const canWrite = useCan("users.write");
  const { data: me } = useMe();
  const [term, setTerm] = useState(search.q ?? "");
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<AdminUser | null>(null);

  const users = useQuery({
    queryKey: ["users", search],
    queryFn: () =>
      api<Paginated<AdminUser>>("/admin/users", { query: { ...search, pageSize: 50 } }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["users"] });

  const update = useMutation({
    mutationFn: (user: AdminUser) =>
      api<AdminUser>(`/admin/users/${user.id}`, {
        method: "PATCH",
        body: { name: user.name, role: user.role, banned: user.banned },
      }),
    onSuccess: async () => {
      setEditing(null);
      await invalidate();
      toast.success("User updated");
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api<void>(`/admin/users/${id}/revoke-sessions`, { method: "POST" }),
    onSuccess: () => {
      setRevoking(null);
      toast.success("Sessions revoked");
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const sendReset = useMutation({
    mutationFn: (id: string) => api<void>(`/admin/users/${id}/password-reset`, { method: "POST" }),
    onSuccess: () => toast.success("Password reset email sent"),
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (users.error) return <ErrorState error={users.error} onRetry={() => void users.refetch()} />;

  return (
    <>
      <PageHeader
        title="Users"
        description="Accounts and staff roles. Roles decide what each screen allows."
        actions={
          canWrite && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <PlusIcon /> New user
            </Button>
          )
        }
      />

      <Toolbar>
        <form
          className="flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void navigate({
              search: (previous) => ({ ...previous, page: 1, q: term || undefined }),
            });
          }}
        >
          <Input
            placeholder="Search name or email"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            className="w-64"
          />
          <Button type="submit">Search</Button>
        </form>
        <Select
          value={search.role ?? ""}
          aria-label="Role filter"
          onChange={(event) =>
            void navigate({
              search: (previous) => ({
                ...previous,
                page: 1,
                role: (event.target.value || undefined) as typeof search.role,
              }),
            })
          }
        >
          <option value="">All roles</option>
          {ROLES.map((role) => (
            <option key={role} value={role}>
              {role}
            </option>
          ))}
        </Select>
      </Toolbar>

      <div className="border-line bg-surface rounded-card overflow-hidden border">
        {users.isPending ? (
          <Loading />
        ) : users.data.items.length === 0 ? (
          <EmptyState>No users match this filter.</EmptyState>
        ) : (
          <table className="text-ui w-full border-collapse text-left">
            <thead className="bg-well">
              <tr className="border-line border-b">
                <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">User</th>
                <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">Role</th>
                <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">State</th>
                <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">
                  Registered
                </th>
                <th className="h-9 px-3" />
              </tr>
            </thead>
            <tbody>
              {users.data.items.map((user) => (
                <tr key={user.id} className="t-row border-line/60 border-b last:border-0">
                  <td className="h-10 px-3">
                    <p className="text-ink font-medium">{user.name}</p>
                    <p className="text-muted text-caption">{user.email}</p>
                  </td>
                  <td className="px-3">
                    <Badge tone={user.role === "admin" ? "info" : "neutral"}>{user.role}</Badge>
                  </td>
                  <td className="px-3">
                    <div className="flex gap-1">
                      {user.banned && <Badge tone="danger">banned</Badge>}
                      {!user.emailVerified && <Badge tone="warning">unverified</Badge>}
                      {user.legacyId !== null && <Badge>legacy #{user.legacyId}</Badge>}
                    </div>
                  </td>
                  <td className="text-muted text-caption px-3">{formatDateTime(user.createdAt)}</td>
                  <td className="px-3">
                    {canWrite && (
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => sendReset.mutate(user.id)}>
                          Reset password
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setRevoking(user)}>
                          Revoke sessions
                        </Button>
                        <Button size="sm" onClick={() => setEditing(user)}>
                          Edit
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination
          page={users.data?.page ?? 1}
          pageSize={users.data?.pageSize ?? 50}
          total={users.data?.total ?? 0}
          onPage={(page) => void navigate({ search: (previous) => ({ ...previous, page }) })}
        />
      </div>

      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing ? `Edit ${editing.name}` : "Edit user"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={update.isPending}
              onClick={() => editing && update.mutate(editing)}
            >
              Save
            </Button>
          </>
        }
      >
        {editing && (
          <div className="flex flex-col gap-3">
            <Field label="Name">
              <Input
                value={editing.name}
                onChange={(event) => setEditing({ ...editing, name: event.target.value })}
              />
            </Field>
            <Field
              label="Role"
              hint={editing.id === me?.id ? "You cannot change your own role." : undefined}
            >
              <Select
                value={editing.role}
                disabled={editing.id === me?.id}
                onChange={(event) =>
                  setEditing({ ...editing, role: event.target.value as AdminUser["role"] })
                }
              >
                {ROLES.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </Select>
            </Field>
            <Checkbox
              label="Banned"
              checked={editing.banned}
              disabled={editing.id === me?.id}
              onChange={(event) => setEditing({ ...editing, banned: event.target.checked })}
            />
          </div>
        )}
      </Dialog>

      {creating && (
        <CreateUserDialog
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await invalidate();
          }}
        />
      )}

      <ConfirmDialog
        open={revoking !== null}
        title="Revoke sessions"
        message={`Sign ${revoking?.name} out of every device?`}
        confirmLabel="Revoke"
        danger
        pending={revoke.isPending}
        onConfirm={() => revoking && revoke.mutate(revoking.id)}
        onClose={() => setRevoking(null)}
      />
    </>
  );
}

function CreateUserDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AdminUser["role"]>("editor");
  const [password, setPassword] = useState("");

  const create = useMutation({
    mutationFn: () => api<AdminUser>("/admin/users", { body: { name, email, role, password } }),
    onSuccess: () => {
      toast.success("User created");
      onSaved();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="New user"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={create.isPending} onClick={() => create.mutate()}>
            Create
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Name">
          <Input value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field label="Email">
          <Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
        </Field>
        <Field label="Role">
          <Select
            value={role}
            onChange={(event) => setRole(event.target.value as AdminUser["role"])}
          >
            {ROLES.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Temporary password" hint="At least 12 characters; share it out of band.">
          <Input
            type="text"
            autoComplete="off"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}
